/**
 * BlackoutSpecV2 — mirrors the hub shape (atlas-hub/services/hub/src/schedules/blackout.ts).
 *
 * No shared protocol package on this surface, so the type is duplicated. Keep
 * in sync with the hub side; both pass through the same JSONB column.
 *
 * Also exports `compileToBlackoutSpec` / `seedFromBlackoutSpec` to convert
 * between `RecurrenceValue` (the meeting-style form's internal shape) and
 * `BlackoutSpecV2` (the wire shape).
 */

import * as rrulePkg from "rrule";

// rrule ships dual CJS/ESM with mismatched export shapes; namespace import
// works for both Vite (ESM) and Node-test (tsx/CJS interop).
const { RRule } = (rrulePkg as { RRule: typeof import("rrule").RRule } & { default?: { RRule: typeof import("rrule").RRule } }).default ?? rrulePkg;

import type { RecurrenceValue } from "../components/RecurrenceEditor.js";
import { EMPTY_RECURRENCE } from "../components/RecurrenceEditor.js";

export interface BlackoutSpecV2 {
  /** RRULE body (without leading `RRULE:`), or null for a one-time window */
  rrule: string | null;
  /** ISO 8601 datetime — first/only occurrence start */
  dtstart: string;
  /** Window length in minutes (1..525600) */
  duration_minutes: number;
  /** IANA timezone */
  timezone: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const RRULE_FREQ_MAP = {
  minutely: "MINUTELY",
  hourly: "HOURLY",
  daily: "DAILY",
  weekly: "WEEKLY",
  monthly: "MONTHLY",
  yearly: "YEARLY",
} as const;

function parseHHMM(t: string): [number, number] {
  const [h = "0", m = "0"] = t.split(":");
  return [parseInt(h, 10), parseInt(m, 10)];
}

function parseDate(d: string): [number, number, number] {
  const [y = "0", m = "0", day = "0"] = d.split("-");
  return [parseInt(y, 10), parseInt(m, 10), parseInt(day, 10)];
}

function spanMinutes(sd: string, st: string, ed: string, et: string, allDay: boolean): number {
  const [sy, sm, sdd] = parseDate(sd);
  const [ey, em, edd] = parseDate(ed);
  if (allDay) {
    const dayDiff = (Date.UTC(ey, em - 1, edd) - Date.UTC(sy, sm - 1, sdd)) / 86_400_000;
    return Math.max(1, (dayDiff + 1) * 1440);
  }
  const [sh, smin] = parseHHMM(st);
  const [eh, emin] = parseHHMM(et);
  const startMs = Date.UTC(sy, sm - 1, sdd, sh, smin);
  const endMs = Date.UTC(ey, em - 1, edd, eh, emin);
  return Math.max(1, Math.round((endMs - startMs) / 60_000));
}

function localDateTimeISO(d: string, t: string): string {
  const [y, m, day] = parseDate(d);
  const [h, min] = parseHHMM(t);
  // Render as a timezone-naive ISO datetime; the timezone field on
  // BlackoutSpecV2 carries the IANA zone separately.
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${y}-${pad(m)}-${pad(day)}T${pad(h)}:${pad(min)}:00`;
}

function rruleUntilFromDate(d: string): string {
  const [y, m, day] = parseDate(d);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${y}${pad(m)}${pad(day)}T235959Z`;
}

// ---------------------------------------------------------------------------
// RecurrenceValue → BlackoutSpecV2
// ---------------------------------------------------------------------------

export function compileToBlackoutSpec(v: RecurrenceValue): BlackoutSpecV2 {
  const dtstart = localDateTimeISO(v.start_date, v.all_day ? "00:00" : v.start_time);
  const duration_minutes = spanMinutes(
    v.start_date,
    v.start_time,
    v.end_date || v.start_date,
    v.end_time,
    v.all_day
  );

  if (!v.is_recurring) {
    return { rrule: null, dtstart, duration_minutes, timezone: v.timezone };
  }

  const parts: string[] = [`FREQ=${RRULE_FREQ_MAP[v.granularity]}`];
  if (v.interval > 1) parts.push(`INTERVAL=${v.interval}`);

  // Daily filters which weekdays the rule fires on. Weekly is anchored to
  // dtstart's weekday — we deliberately do NOT emit BYDAY for weekly.
  if (v.granularity === "daily" && v.byday.length > 0 && v.byday.length < 7) {
    parts.push(`BYDAY=${v.byday.join(",")}`);
  }
  if (v.granularity === "monthly" && v.monthly_last_day) {
    parts.push("BYMONTHDAY=-1");
  }
  if (v.end_mode === "on_date" && v.end_date_recur) {
    parts.push(`UNTIL=${rruleUntilFromDate(v.end_date_recur)}`);
  } else if (v.end_mode === "after_n") {
    parts.push(`COUNT=${Math.max(1, v.end_count)}`);
  }

  return {
    rrule: parts.join(";"),
    dtstart,
    duration_minutes,
    timezone: v.timezone,
  };
}

// ---------------------------------------------------------------------------
// BlackoutSpecV2 → RecurrenceValue (round-trip seed for editing)
// ---------------------------------------------------------------------------

function isoToLocalDateTime(iso: string): { date: string; time: string } {
  // Strip timezone if present and split.
  const cleaned = iso.replace(/Z$/, "").replace(/[+-]\d{2}:?\d{2}$/, "");
  const [datePart = "", timePart = "00:00"] = cleaned.split("T");
  const [hh = "00", mm = "00"] = timePart.split(":");
  return { date: datePart, time: `${hh}:${mm}` };
}

function parseRruleField(rrule: string): Map<string, string> {
  const out = new Map<string, string>();
  for (const part of rrule.split(";")) {
    const eq = part.indexOf("=");
    if (eq > 0) out.set(part.slice(0, eq).toUpperCase(), part.slice(eq + 1));
  }
  return out;
}

/** True if `spec` is a pre-ADR-0072 legacy shape that wasn't migrated. */
function isLegacySpec(spec: unknown): boolean {
  return !!spec
    && typeof spec === "object"
    && typeof (spec as { dtstart?: unknown }).dtstart !== "string";
}

export function seedFromBlackoutSpec(spec: BlackoutSpecV2): RecurrenceValue {
  // Legacy rows that escaped migration 117 — drop the user back to a fresh form
  // rather than crashing the editor. Saving will store the new V2 shape.
  if (isLegacySpec(spec)) {
    return { ...EMPTY_RECURRENCE };
  }
  const { date: startDate, time: startTime } = isoToLocalDateTime(spec.dtstart);
  // Compute end date+time from start + duration_minutes.
  const [y, m, d] = parseDate(startDate);
  const [h, min] = parseHHMM(startTime);
  const endMs = Date.UTC(y, m - 1, d, h, min) + spec.duration_minutes * 60_000;
  const endDt = new Date(endMs);
  const endDate = endDt.toISOString().slice(0, 10);
  const endTime = endDt.toISOString().slice(11, 16);

  const base: RecurrenceValue = {
    ...EMPTY_RECURRENCE,
    timezone: spec.timezone || EMPTY_RECURRENCE.timezone,
    start_date: startDate,
    start_time: startTime,
    end_date: endDate,
    end_time: endTime,
    all_day: spec.duration_minutes % 1440 === 0,
  };

  if (!spec.rrule) {
    return { ...base, is_recurring: false };
  }

  const fields = parseRruleField(spec.rrule);
  const freq = (fields.get("FREQ") ?? "WEEKLY").toLowerCase();
  const rawByday = (fields.get("BYDAY") ?? "").split(",").filter((x) => x.length > 0);

  // Map RRULE FREQ → form granularity.
  let granularity: RecurrenceValue["granularity"];
  let byday: string[];
  if (freq === "minutely") {
    granularity = "minutely"; byday = [];
  } else if (freq === "hourly") {
    granularity = "hourly"; byday = [];
  } else if (freq === "monthly") {
    granularity = "monthly"; byday = [];
  } else if (freq === "yearly") {
    granularity = "yearly"; byday = [];
  } else if (freq === "daily" || (freq === "weekly" && rawByday.length > 1)) {
    // FREQ=WEEKLY with multiple BYDAY entries (legacy V2 from a pre-redesign
    // form) seeds as daily; otherwise weekly (anchored to dtstart, no chips).
    granularity = "daily"; byday = rawByday.length > 0 ? rawByday : ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];
  } else {
    granularity = "weekly"; byday = [];
  }

  const interval = parseInt(fields.get("INTERVAL") ?? "1", 10) || 1;
  const monthly_last_day = fields.get("BYMONTHDAY") === "-1";

  let end_mode: RecurrenceValue["end_mode"] = "none";
  let end_date_recur = "";
  let end_count = base.end_count;
  if (fields.has("UNTIL")) {
    end_mode = "on_date";
    const u = fields.get("UNTIL")!;
    end_date_recur = `${u.slice(0, 4)}-${u.slice(4, 6)}-${u.slice(6, 8)}`;
  } else if (fields.has("COUNT")) {
    end_mode = "after_n";
    end_count = Math.max(1, parseInt(fields.get("COUNT")!, 10) || 1);
  }

  return {
    ...base,
    is_recurring: true,
    granularity,
    interval,
    byday,
    monthly_last_day,
    end_mode,
    end_date_recur,
    end_count,
  };
}

/** Render a one-line English summary of a BlackoutSpecV2 for list views. */
export function describeBlackoutSpec(spec: BlackoutSpecV2): string {
  if (isLegacySpec(spec)) {
    return "Legacy spec — open and re-save to migrate";
  }
  const dur = formatDurationMinutes(spec.duration_minutes);
  const { date, time } = isoToLocalDateTime(spec.dtstart);
  if (!spec.rrule) {
    return `${date} ${time} · ${dur} · ${spec.timezone}`;
  }
  try {
    const rule = RRule.fromString(`RRULE:${spec.rrule}`);
    return `${rule.toText()} · ${dur} · ${spec.timezone}`;
  } catch {
    return `Recurring · ${dur} · ${spec.timezone}`;
  }
}

function formatDurationMinutes(mins: number): string {
  if (mins <= 0) return "—";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}
